/* eslint-disable react-hooks/set-state-in-effect */
import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Search, User, Mail, Phone, Lock, Shield, UserCog, UserX, UserCheck, Trash2, UserPlus, Stethoscope, Euro, Pencil, Users, MailCheck, Send, Clock } from 'lucide-react';
import logger from '@/lib/logger';
import { PageTitle } from '@/components/backoffice/PageTitle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_MAP = {
  admin: { label: 'Admin', className: 'bg-purple-100 text-purple-800' },
  operator: { label: 'Operatore', className: 'bg-blue-100 text-blue-800' },
  doctor: { label: 'Dottore', className: 'bg-teal-100 text-teal-800' },
  patient: { label: 'Paziente', className: 'bg-green-100 text-green-800' },
};

const EMPTY_CREATE = { email: '', password: '', nome: '', cognome: '', telefono: '', role: 'patient', doctor_id: '' };

const STATUS_MAP = {
  pending: { label: 'In Attesa', className: 'bg-[hsl(var(--warning))] text-[hsl(var(--warning-foreground))]' },
  confirmed: { label: 'Confermato', className: 'bg-[hsl(var(--success))] text-[hsl(var(--success-foreground))]' },
  rejected: { label: 'Rifiutato', className: 'bg-[hsl(var(--destructive))] text-[hsl(var(--destructive-foreground))]' },
  cancelled: { label: 'Annullato', className: 'bg-slate-500 text-white' },
  completed: { label: 'Completato', className: 'bg-[hsl(var(--info))] text-[hsl(var(--info-foreground))]' },
};

export default function Pazienti({ patientsOnly = false }) {
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'admin';

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState(patientsOnly ? 'patient' : 'all');
  const [editDialog, setEditDialog] = useState(null);
  const [editForm, setEditForm] = useState({ nome: '', cognome: '', email: '', telefono: '' });
  const [savingEdit, setSavingEdit] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [resetPwDialog, setResetPwDialog] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [roleDialog, setRoleDialog] = useState(null);
  const [selectedRole, setSelectedRole] = useState('');
  const [roleDoctorId, setRoleDoctorId] = useState('');
  const [doctors, setDoctors] = useState([]);
  const [createDialog, setCreateDialog] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_CREATE);
  const [creating, setCreating] = useState(false);

  const fetchDoctors = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/doctors?active_only=false`);
      setDoctors(res.data);
    } catch (err) { logger.error('Doctors error:', err); }
  }, []);

  const doctorLabel = (id) => {
    const d = doctors.find(x => x.id === id);
    return d ? `Dott. ${d.nome} ${d.cognome}` : null;
  };

  // doctors not yet linked to another user (selectable), plus the current one being edited
  const availableDoctors = (currentLinkedId) => doctors.filter(d => !d.user_id || d.id === currentLinkedId);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const effectiveRole = patientsOnly ? 'patient' : roleFilter;
      const res = await axios.get(`${API}/patients`, { params: { search: search || undefined, role: effectiveRole !== 'all' ? effectiveRole : undefined } });
      // Gestione Utenti = solo staff (admin, operatori, dottori); i pazienti hanno la loro pagina dedicata
      setUsers(patientsOnly ? res.data : res.data.filter(u => u.role !== 'patient'));
    } catch (err) { logger.error('Fetch error:', err); } finally { setLoading(false); }
  }, [search, roleFilter, patientsOnly]);

  useEffect(() => { fetchUsers(); }, [roleFilter, fetchUsers]);
  useEffect(() => { fetchDoctors(); }, [fetchDoctors]);

  const handleSearch = (e) => { e.preventDefault(); fetchUsers(); };

  const openDetail = async (userId) => {
    setDetailLoading(true);
    setDetail(null);
    try {
      const res = await axios.get(`${API}/patients/${userId}`, {});
      setDetail(res.data);
    } catch { setDetail({ patient: users.find(u => u.id === userId), appointments: [] }); }
    finally { setDetailLoading(false); }
  };

  const handleResetPassword = async () => {
    try {
      await axios.put(`${API}/users/${resetPwDialog.id}/reset-password?new_password=${encodeURIComponent(newPassword)}`, {}, {});
      toast.success(`Password reimpostata per ${resetPwDialog.email}`);
      setResetPwDialog(null);
      setNewPassword('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };

  const handleChangeRole = async () => {
    if (selectedRole === 'doctor' && !roleDoctorId) { toast.error('Seleziona il profilo dottore da collegare'); return; }
    try {
      const params = new URLSearchParams({ new_role: selectedRole });
      if (selectedRole === 'doctor') params.append('doctor_id', roleDoctorId);
      await axios.put(`${API}/users/${roleDialog.id}/role?${params.toString()}`, {}, {});
      toast.success(`Ruolo aggiornato a ${ROLE_MAP[selectedRole]?.label}`);
      setRoleDialog(null);
      fetchUsers();
      fetchDoctors();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };

  const openEdit = (u) => {
    setEditForm({ nome: u.nome || '', cognome: u.cognome || '', email: u.email || '', telefono: u.telefono || '' });
    setEditDialog(u);
  };

  const handleSaveEdit = async () => {
    if (!editForm.nome.trim() || !editForm.cognome.trim() || !editForm.email.trim()) { toast.error('Nome, cognome ed email sono obbligatori'); return; }
    setSavingEdit(true);
    try {
      await axios.put(`${API}/users/${editDialog.id}`, {
        nome: editForm.nome.trim(), cognome: editForm.cognome.trim(), email: editForm.email.trim(), telefono: editForm.telefono.trim()
      });
      toast.success('Dati utente aggiornati');
      setEditDialog(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel salvataggio');
    } finally { setSavingEdit(false); }
  };

  const canEdit = (u) => isAdmin || u.role === 'patient';

  // Account creato dallo staff e password mai impostata dal paziente
  const needsActivation = (u) => u?.role === 'patient' && !!u.created_by_staff && !u.password_set_at;
  const [resendingId, setResendingId] = useState(null);
  const resendActivation = async (u) => {
    setResendingId(u.id);
    try {
      const res = await axios.post(`${API}/patients/${u.id}/resend-activation`);
      toast.success(res.data.message);
      setUsers(prev => prev.map(x => x.id === u.id ? { ...x, activation_sent_at: res.data.activation_sent_at } : x));
      if (detail?.patient?.id === u.id) setDetail(d => ({ ...d, patient: { ...d.patient, activation_sent_at: res.data.activation_sent_at } }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invio non riuscito');
    } finally { setResendingId(null); }
  };
  const fmtDT = (iso) => iso ? new Date(iso).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '';

  const handleCreateUser = async () => {
    const f = createForm;
    if (!f.email || !f.nome || !f.cognome) { toast.error('Compila email, nome e cognome'); return; }
    if (!f.password || f.password.length < 6) { toast.error('La password deve avere almeno 6 caratteri'); return; }
    if (f.role === 'doctor' && !f.doctor_id) { toast.error('Seleziona il profilo dottore da collegare'); return; }
    setCreating(true);
    try {
      await axios.post(`${API}/users/create`, {
        email: f.email.trim(),
        password: f.password,
        nome: f.nome.trim(),
        cognome: f.cognome.trim(),
        telefono: f.telefono || null,
        role: f.role,
        doctor_id: f.role === 'doctor' ? f.doctor_id : null,
      });
      toast.success('Utente creato');
      setCreateDialog(false);
      setCreateForm(EMPTY_CREATE);
      fetchUsers();
      fetchDoctors();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nella creazione');
    } finally {
      setCreating(false);
    }
  };

  const counts = {
    all: users.length,
    admin: users.filter(u => u.role === 'admin').length,
    operator: users.filter(u => u.role === 'operator').length,
    patient: users.filter(u => u.role === 'patient').length,
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <PageTitle title={patientsOnly ? 'Gestione Pazienti' : 'Gestione Utenti'} info={patientsOnly ? 'Anagrafica dei pazienti registrati: modifica, storico appuntamenti e gestione account.' : 'Utenti dello staff: admin, operatori e dottori. I pazienti si gestiscono dalla voce "Pazienti".'} testId="users-page-title" />
        </div>
        {(isAdmin || patientsOnly) && (
          <Button onClick={() => { setCreateForm({ ...EMPTY_CREATE, role: patientsOnly ? 'patient' : 'operator' }); setCreateDialog(true); }} className="bg-[#238A92] hover:bg-[#0F5E66] text-white" data-testid="user-create-button">
            <UserPlus size={16} className="mr-1" /> {patientsOnly ? 'Nuovo Paziente' : 'Nuovo Utente'}
          </Button>
        )}
      </div>

      {/* Role filter tabs */}
      {!patientsOnly && <div className="flex flex-wrap gap-2 mb-4">
        {[
          { key: 'all', label: 'Tutti' },
          { key: 'admin', label: 'Admin' },
          { key: 'operator', label: 'Operatori' },
          { key: 'doctor', label: 'Dottori' },
        ].map(f => (
          <Button key={f.key} size="sm" variant={roleFilter === f.key ? 'default' : 'outline'} onClick={() => setRoleFilter(f.key)} className={roleFilter === f.key ? 'bg-[#238A92] text-white' : ''} data-testid={`users-role-filter-${f.key}`}>
            {f.label}
          </Button>
        ))}
      </div>}

      <Card className="mb-6">
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className="flex gap-3">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input placeholder="Cerca per nome, cognome o email..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" data-testid="users-search-input" />
            </div>
            <Button type="submit" variant="outline">Cerca</Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-12 rounded" />)}</div>
          ) : users.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground" data-testid="users-empty">{patientsOnly ? 'Nessun paziente trovato' : 'Nessun utente trovato'}</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Email</TableHead>
                    {!patientsOnly && <TableHead>Ruolo</TableHead>}
                    <TableHead>Telefono</TableHead>
                    <TableHead>Account</TableHead>
                    <TableHead>Registrato</TableHead>
                    <TableHead className="text-right">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map(u => (
                    <TableRow key={u.id} className={u.active === false ? 'opacity-50' : ''} data-testid={`user-row-${u.id}`}>
                      <TableCell className="font-medium">
                        {u.nome} {u.cognome}
                        {u.active === false && <Badge variant="secondary" className="ml-2 text-[10px]">Disattivato</Badge>}
                        {u.role === 'patient' && u.pag_anticipo === false && <Badge className="ml-2 text-[10px] bg-slate-200 text-slate-700 hover:bg-slate-200" data-testid={`no-anticipo-badge-${u.id}`}>No anticipo</Badge>}
                        {u.role === 'doctor' && u.doctor_id && (
                          <span className="block text-[11px] text-[#238A92] mt-0.5">→ {doctorLabel(u.doctor_id) || 'Profilo dottore'}</span>
                        )}
                        {u.role === 'doctor' && !u.doctor_id && (
                          <span className="block text-[11px] text-amber-600 mt-0.5">Nessun profilo dottore collegato</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">{u.email}</TableCell>
                      {!patientsOnly && <TableCell><Badge className={ROLE_MAP[u.role]?.className}>{ROLE_MAP[u.role]?.label}</Badge></TableCell>}
                      <TableCell className="text-sm">{u.telefono || '-'}</TableCell>
                      <TableCell data-testid={`user-activation-${u.id}`}>
                        {u.role !== 'patient' ? (
                          <span className="text-xs text-muted-foreground">—</span>
                        ) : needsActivation(u) ? (
                          <div className="flex flex-col items-start gap-0.5">
                            <Badge className="bg-amber-100 text-amber-900 hover:bg-amber-100 border border-amber-300 text-[10px] gap-1"><Clock size={10} /> Da attivare</Badge>
                            {u.activation_sent_at && <span className="text-[10px] text-muted-foreground">link inviato {fmtDT(u.activation_sent_at)}</span>}
                          </div>
                        ) : (
                          <Badge className="bg-green-100 text-green-800 hover:bg-green-100 text-[10px] gap-1"><MailCheck size={10} /> Attivo</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{u.created_at ? new Date(u.created_at).toLocaleDateString('it-IT') : '-'}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {u.role === 'patient' && (
                            <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => openDetail(u.id)} title="Dettagli" data-testid={`user-detail-btn-${u.id}`}>
                              <User size={14} />
                            </Button>
                          )}
                          {needsActivation(u) && (
                            <Button size="sm" variant="ghost" className="h-8 px-2 text-amber-700 hover:bg-amber-50" onClick={() => resendActivation(u)} disabled={resendingId === u.id} title="Reinvia link per impostare la password" data-testid={`user-resend-activation-btn-${u.id}`}>
                              <Send size={14} />
                            </Button>
                          )}
                          {canEdit(u) && (
                            <Button size="sm" variant="ghost" className="h-8 px-2 text-[#238A92]" onClick={() => openEdit(u)} title="Modifica dati" data-testid={`user-edit-btn-${u.id}`}>
                              <Pencil size={14} />
                            </Button>
                          )}
                          {u.role === 'patient' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className={`h-8 px-2 ${u.pag_anticipo !== false ? 'text-[#1E7A58]' : 'text-slate-400'}`}
                              title={u.pag_anticipo !== false ? 'Richiesta anticipo ATTIVA — clicca per disattivare' : 'Richiesta anticipo DISATTIVATA — clicca per attivare'}
                              data-testid={`pag-anticipo-toggle-${u.id}`}
                              onClick={async () => {
                                try {
                                  const res = await axios.put(`${API}/users/${u.id}/pag-anticipo`);
                                  toast.success(res.data.message);
                                  fetchUsers();
                                } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
                              }}
                            >
                              <Euro size={14} />
                            </Button>
                          )}
                          {isAdmin && (
                            <>
                              {!patientsOnly && (
                                <Button size="sm" variant="ghost" className="h-8 px-2 text-blue-600" onClick={() => { setRoleDialog({ id: u.id, email: u.email, currentRole: u.role }); setSelectedRole(u.role); setRoleDoctorId(u.doctor_id || ''); }} title="Cambia Ruolo" data-testid={`user-role-btn-${u.id}`}>
                                  <UserCog size={14} />
                                </Button>
                              )}
                              <Button size="sm" variant="ghost" className="h-8 px-2 text-amber-600" onClick={() => setResetPwDialog({ id: u.id, email: u.email })} title="Reset Password" data-testid={`user-resetpw-btn-${u.id}`}>
                                <Lock size={14} />
                              </Button>
                              {u.active !== false ? (
                                <Button size="sm" variant="ghost" className="h-8 px-2 text-orange-500" title="Disattiva"
                                  onClick={async () => {
                                    try {
                                      await axios.put(`${API}/users/${u.id}/deactivate`);
                                      toast.success(`${u.nome} ${u.cognome} disattivato`);
                                      fetchUsers();
                                    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
                                  }}>
                                  <UserX size={14} />
                                </Button>
                              ) : (
                                <Button size="sm" variant="ghost" className="h-8 px-2 text-green-600" title="Riattiva"
                                  onClick={async () => {
                                    try {
                                      await axios.put(`${API}/users/${u.id}/activate`);
                                      toast.success(`${u.nome} ${u.cognome} riattivato`);
                                      fetchUsers();
                                    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
                                  }}>
                                  <UserCheck size={14} />
                                </Button>
                              )}
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button size="sm" variant="ghost" className="h-8 px-2 text-red-600" title="Elimina definitivamente" data-testid={`user-delete-btn-${u.id}`}>
                                    <Trash2 size={14} />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Elimina Utente Definitivamente</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Stai per eliminare <strong>{u.nome} {u.cognome}</strong> ({u.email}) definitivamente. Questa azione è irreversibile e cancellerà tutti i dati dell&apos;utente. Lo storico degli appuntamenti rimarrà ma senza riferimento all&apos;utente.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Annulla</AlertDialogCancel>
                                    <AlertDialogAction className="bg-red-600" data-testid="user-delete-confirm-btn" onClick={async () => {
                                      try {
                                        await axios.delete(`${API}/users/${u.id}`);
                                        toast.success(`Utente eliminato definitivamente`);
                                        fetchUsers();
                                      } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
                                    }}>Elimina Definitivamente</AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Patient Detail Dialog */}
      <Dialog open={!!detail || detailLoading} onOpenChange={() => setDetail(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle style={{fontFamily:'Space Grotesk'}}>Dettaglio Utente</DialogTitle></DialogHeader>
          {detailLoading ? (
            <div className="space-y-3"><Skeleton className="h-6 w-48" /><Skeleton className="h-4 w-32" /><Skeleton className="h-40" /></div>
          ) : detail && (
            <div className="space-y-4">
              <div className="bg-[hsl(var(--secondary))] rounded-xl p-4 space-y-2">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 rounded-full bg-[#238A92] flex items-center justify-center text-white"><User size={20} /></div>
                  <div>
                    <p className="font-semibold" style={{fontFamily:'Space Grotesk'}}>{detail.patient?.nome} {detail.patient?.cognome}</p>
                    <Badge className={ROLE_MAP[detail.patient?.role]?.className}>{ROLE_MAP[detail.patient?.role]?.label}</Badge>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-sm"><Mail size={14} className="text-muted-foreground" /> {detail.patient?.email}</div>
                <div className="flex items-center gap-2 text-sm"><Phone size={14} className="text-muted-foreground" /> {detail.patient?.telefono || 'N/D'}</div>
              </div>
              {needsActivation(detail.patient) && (
                <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="detail-activation-box">
                  <div className="flex-1 text-sm">
                    <p className="font-semibold text-amber-900 flex items-center gap-1.5"><Clock size={14} /> Account non ancora attivato</p>
                    <p className="text-xs text-amber-800 mt-0.5">Il paziente non ha ancora impostato la password.{detail.patient?.activation_sent_at ? ` Ultimo link inviato il ${fmtDT(detail.patient.activation_sent_at)}.` : ' Nessun link inviato finora.'}</p>
                  </div>
                  <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white flex-shrink-0" onClick={() => resendActivation(detail.patient)} disabled={resendingId === detail.patient?.id} data-testid="detail-resend-activation-btn">
                    <Send size={14} className="mr-1" /> {resendingId === detail.patient?.id ? 'Invio...' : 'Reinvia link password'}
                  </Button>
                </div>
              )}
              {detail.appointments && detail.appointments.length > 0 && (
                <div>
                  <h4 className="font-semibold text-sm mb-3" style={{fontFamily:'Space Grotesk'}}>Storico Appuntamenti ({detail.appointments.length})</h4>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {detail.appointments.map(a => (
                      <div key={a.id} className="flex items-center justify-between border rounded-lg p-3">
                        <div>
                          <p className="text-sm font-medium">{a.service_nome}</p>
                          <p className="text-xs text-muted-foreground">{a.data} alle {a.ora} - Dott. {a.doctor_cognome}</p>
                        </div>
                        <Badge className={STATUS_MAP[a.status]?.className}>{STATUS_MAP[a.status]?.label}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={!!editDialog} onOpenChange={() => setEditDialog(null)}>
        <DialogContent className="max-w-md" data-testid="user-edit-dialog">
          <DialogHeader><DialogTitle style={{fontFamily:'Space Grotesk'}}>Modifica {editDialog?.role === 'patient' ? 'Paziente' : 'Utente'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="edit-nome">Nome</Label>
                <Input id="edit-nome" value={editForm.nome} onChange={e => setEditForm(p => ({ ...p, nome: e.target.value }))} data-testid="edit-nome-input" />
              </div>
              <div className="space-y-1">
                <Label htmlFor="edit-cognome">Cognome</Label>
                <Input id="edit-cognome" value={editForm.cognome} onChange={e => setEditForm(p => ({ ...p, cognome: e.target.value }))} data-testid="edit-cognome-input" />
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="edit-email">Email</Label>
              <Input id="edit-email" type="email" value={editForm.email} onChange={e => setEditForm(p => ({ ...p, email: e.target.value }))} data-testid="edit-email-input" />
              <p className="text-xs text-muted-foreground">L'utente userà la nuova email per accedere.</p>
            </div>
            <div className="space-y-1">
              <Label htmlFor="edit-telefono">Telefono</Label>
              <Input id="edit-telefono" type="tel" value={editForm.telefono} onChange={e => setEditForm(p => ({ ...p, telefono: e.target.value }))} placeholder="+39 333 1234567" data-testid="edit-telefono-input" />
              <p className="text-xs text-muted-foreground">Necessario per la comunicazione via WhatsApp.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditDialog(null)}>Annulla</Button>
            <Button className="bg-[#238A92] hover:bg-[#0F5E66] text-white" onClick={handleSaveEdit} disabled={savingEdit} data-testid="edit-user-save-btn">
              {savingEdit ? 'Salvataggio...' : 'Salva Modifiche'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={!!resetPwDialog} onOpenChange={() => { setResetPwDialog(null); setNewPassword(''); }}>
        <DialogContent>
          <DialogHeader><DialogTitle style={{fontFamily:'Space Grotesk'}}>Reimposta Password</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Reimposta la password per: <strong>{resetPwDialog?.email}</strong></p>
            <div className="space-y-2">
              <Label>Nuova password</Label>
              <Input type="text" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Minimo 6 caratteri" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setResetPwDialog(null); setNewPassword(''); }}>Annulla</Button>
            <Button disabled={newPassword.length < 6} className="bg-[#238A92] hover:bg-[#0F5E66] text-white" onClick={handleResetPassword}>
              <Lock size={14} className="mr-1" /> Reimposta
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Role Dialog */}
      <Dialog open={!!roleDialog} onOpenChange={() => setRoleDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle style={{fontFamily:'Space Grotesk'}}>Cambia Ruolo Utente</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Utente: <strong>{roleDialog?.email}</strong></p>
            <p className="text-sm text-muted-foreground">Ruolo attuale: <Badge className={ROLE_MAP[roleDialog?.currentRole]?.className}>{ROLE_MAP[roleDialog?.currentRole]?.label}</Badge></p>
            <div className="space-y-2">
              <Label>Nuovo ruolo</Label>
              <select value={selectedRole} onChange={e => setSelectedRole(e.target.value)} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="role-select">
                <option value="patient">Paziente</option>
                <option value="operator">Operatore</option>
                <option value="doctor">Dottore</option>
                <option value="admin">Admin</option>
              </select>
              {selectedRole === 'doctor' && (
                <div className="space-y-1 mt-3">
                  <Label className="flex items-center gap-1.5"><Stethoscope size={14} className="text-[#238A92]" /> Collega al profilo dottore</Label>
                  <select value={roleDoctorId} onChange={e => setRoleDoctorId(e.target.value)} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="role-doctor-select">
                    <option value="">— Seleziona dottore —</option>
                    {availableDoctors(roleDoctorId).map(d => (
                      <option key={d.id} value={d.id}>Dott. {d.nome} {d.cognome}{d.specializzazione ? ` (${d.specializzazione})` : ''}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="text-xs text-muted-foreground space-y-1 mt-2">
                <p><strong>Paziente:</strong> può prenotare e vedere i propri appuntamenti</p>
                <p><strong>Operatore:</strong> può gestire appuntamenti e visualizzare pazienti</p>
                <p><strong>Dottore:</strong> accede alla propria area (disponibilità + propri appuntamenti)</p>
                <p><strong>Admin:</strong> accesso completo (CRUD dottori, servizi, impostazioni, utenti)</p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRoleDialog(null)}>Annulla</Button>
            <Button className="bg-[#238A92] hover:bg-[#0F5E66] text-white" onClick={handleChangeRole} disabled={selectedRole === roleDialog?.currentRole && selectedRole !== 'doctor'} data-testid="role-save-button">
              <Shield size={14} className="mr-1" /> Salva Ruolo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent className="max-w-md max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle style={{fontFamily:'Space Grotesk'}}>{patientsOnly ? 'Nuovo Paziente' : 'Nuovo Utente'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Nome</Label>
                <Input value={createForm.nome} onChange={e => setCreateForm(p => ({ ...p, nome: e.target.value }))} data-testid="create-nome-input" />
              </div>
              <div className="space-y-1">
                <Label>Cognome</Label>
                <Input value={createForm.cognome} onChange={e => setCreateForm(p => ({ ...p, cognome: e.target.value }))} data-testid="create-cognome-input" />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Email</Label>
              <Input type="email" value={createForm.email} onChange={e => setCreateForm(p => ({ ...p, email: e.target.value }))} placeholder="email@esempio.com" data-testid="create-email-input" />
            </div>
            <div className="space-y-1">
              <Label>Telefono</Label>
              <Input type="tel" value={createForm.telefono} onChange={e => setCreateForm(p => ({ ...p, telefono: e.target.value }))} placeholder="+39 333 1234567" data-testid="create-telefono-input" />
              <p className="text-xs text-muted-foreground">Necessario per la comunicazione via WhatsApp.</p>
            </div>
            <div className="space-y-1">
              <Label>Password</Label>
              <Input type="text" value={createForm.password} onChange={e => setCreateForm(p => ({ ...p, password: e.target.value }))} placeholder="Min. 6 caratteri" data-testid="create-password-input" />
            </div>
            {!patientsOnly && <div className="space-y-1">
              <Label>Ruolo</Label>
              <select value={createForm.role} onChange={e => setCreateForm(p => ({ ...p, role: e.target.value, doctor_id: '' }))} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="create-role-select">
                <option value="operator">Operatore</option>
                <option value="doctor">Dottore</option>
                <option value="admin">Admin</option>
              </select>
            </div>}
            {createForm.role === 'doctor' && (
              <div className="space-y-1">
                <Label className="flex items-center gap-1.5"><Stethoscope size={14} className="text-[#238A92]" /> Collega al profilo dottore</Label>
                <select value={createForm.doctor_id} onChange={e => setCreateForm(p => ({ ...p, doctor_id: e.target.value }))} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="create-doctor-select">
                  <option value="">— Seleziona dottore —</option>
                  {availableDoctors('').map(d => (
                    <option key={d.id} value={d.id}>Dott. {d.nome} {d.cognome}{d.specializzazione ? ` (${d.specializzazione})` : ''}</option>
                  ))}
                </select>
                {availableDoctors('').length === 0 && (
                  <p className="text-xs text-amber-600">Tutti i profili dottore sono già collegati a un utente.</p>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreateDialog(false)}>Annulla</Button>
            <Button className="bg-[#238A92] hover:bg-[#0F5E66] text-white" onClick={handleCreateUser} disabled={creating} data-testid="create-user-save">
              {creating ? 'Creazione...' : 'Crea Utente'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
